"use client";

import { useState } from "react";
import type { DiscoveredOrganization } from "stytch";
import { DiscoveredOrganizationsList } from "@/app/(auth)/_components/SelectOrganization";
import { EmailLoginForm } from "@/components/EmailLoginForm";
import { PasswordLoginForm } from "@/components/PasswordLoginForm";
import { AuthenticationStep } from "@/lib/analytics/segmentEvents";
import { trackAuthFlow } from "@/lib/analytics/trackEvents";
import {
  authDiscoveryPassword,
  resetDiscoveryPasswordStart,
  sendDiscoveryEmail,
} from "@/lib/api/auth";
import type { DiscoverySessionData } from "@/lib/SessionServices";

export default function LoginDiscoveryForm({
  passwordLogin,
}: {
  passwordLogin: boolean;
}) {
  const [isPasswordLogin, setIsPasswordLogin] = useState(passwordLogin);
  const [discovery, setDiscovery] = useState<
    | { organizations: DiscoveredOrganization[]; session: DiscoverySessionData }
    | undefined
  >(undefined);

  const handlePasswordLogin = async (
    organizationId: string,
    email: string,
    password: string,
  ) => {
    const resp = await authDiscoveryPassword(email, password);
    if ("discovered_organizations" in resp) {
      trackAuthFlow({
        step: AuthenticationStep.DISCOVERY_PAGE,
        additionalParams: {
          event: "organizations discovered",
          email: email,
          organizationCount: resp.discovered_organizations.length,
          loginMethod: "password",
        },
      });

      setDiscovery({
        organizations: resp.discovered_organizations,
        session: {
          sessionJWT: undefined,
          intermediateSession: resp.intermediate_session_token,
          isDiscovery: true,
          error: false,
        },
      });
    }
    return resp;
  };

  const handleLoginMethodToggle = () => {
    trackAuthFlow({
      step: AuthenticationStep.DISCOVERY_PAGE,
      additionalParams: {
        event: "login method toggled",
        from: isPasswordLogin ? "password" : "email",
        to: isPasswordLogin ? "email" : "password",
      },
    });

    setIsPasswordLogin(!isPasswordLogin);
  };

  if (discovery) {
    return (
      <DiscoveredOrganizationsList
        discovered_organizations={discovery.organizations}
        discoverySessionData={discovery.session}
      />
    );
  }

  return (
    <div className="flex flex-col w-full">
      {isPasswordLogin ? (
        <PasswordLoginForm
          onSubmit={handlePasswordLogin}
          onReset={resetDiscoveryPasswordStart}
          dontRedirect={true}
          organizationId="123"
        />
      ) : (
        <EmailLoginForm onSubmit={sendDiscoveryEmail} />
      )}
      <button
        className="self-center font-nf-ds-mono text-[10.5px] uppercase tracking-[0.12em] text-nf-ds-gray-500 hover:text-nf-ds-gray-800 mt-nf-ds-6 cursor-pointer underline"
        onClick={handleLoginMethodToggle}
        type="button"
      >
        {isPasswordLogin ? "Use email link instead" : "Use password instead"}
      </button>
    </div>
  );
}
